import React, { useState, useEffect, useRef, useCallback } from 'react'; // <<< Důležité: Přidán useCallback
import { 
    getFirestore, 
    doc, 
    setDoc, 
    getDoc, 
    collection, 
    query, 
    onSnapshot, 
    addDoc,
    deleteDoc, 
    getDocs, 
    where, 
    orderBy, // <<< Důležité: Přidán orderBy
    updateDoc // <<< Důležité: Přidán updateDoc
} from 'firebase/firestore'; 

// Import CustomOrderChatModal - assume it's in the same directory or adjust path
// import CustomOrderChatModal from './CustomOrderChatModal'; // Removed - handled in App.js

// --- Main CustomOrders Component ---

// --- Custom Order Modal Component (for creating/editing) ---
const CustomOrderModal = ({ onClose, onSave, orderToEdit, setModalMessage }) => {
    const [title, setTitle] = useState(orderToEdit?.title || '');
    const [description, setDescription] = useState(orderToEdit?.description || '');
    const [budget, setBudget] = useState(orderToEdit?.budget || '');
    const [error, setError] = useState('');

    const handleSave = () => {
        setError('');
        if (!title.trim() || !description.trim() || !budget.trim()) {
            setError('All fields are required.');
            return;
        }
        onSave({ id: orderToEdit?.id, title: title.trim(), description: description.trim(), budget: budget.trim() });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="glass-card p-8 rounded-2xl shadow-2xl w-full max-w-2xl animate-bounceIn border border-white/20 relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5"></div>
                
                <div className="relative">
                    <div className="text-center mb-8">
                        <div className="relative w-20 h-20 mx-auto mb-6">
                            {/* Animated rings */}
                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-600/20 animate-ping" style={{ animationDuration: '2s' }}></div>
                            <div className="absolute inset-2 rounded-2xl bg-gradient-to-br from-blue-500/30 to-purple-600/30 animate-pulse"></div>
                            
                            {/* Main icon */}
                            <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600/30 to-purple-600/30 flex items-center justify-center animate-float">
                                <i className="fas fa-shopping-basket text-blue-400 text-3xl"></i>
                            </div>
                        </div>
                        
                        <h3 className="text-3xl font-bold gradient-text mb-3 animate-slideInLeft">
                            {orderToEdit ? 'Update Your Order' : 'Create Custom Order'}
                        </h3>
                        <p className="text-gray-400 text-sm animate-slideInLeft" style={{ animationDelay: '0.2s' }}>
                            {orderToEdit ? 'Modify your custom development request' : 'Tell us about your dream script and we\'ll make it reality'}
                        </p>
                    </div>

                    {error && (
                        <div className="mb-8 p-4 bg-red-900/20 border border-red-600/30 rounded-xl animate-bounceIn">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                                    <i className="fas fa-exclamation-triangle text-red-400"></i>
                                </div>
                                <div>
                                    <p className="text-red-400 font-semibold text-sm">Validation Error</p>
                                    <p className="text-red-300 text-sm">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-8 animate-slideInLeft" style={{ animationDelay: '0.3s' }}>
                        <div className="relative">
                            <label htmlFor="orderTitle" className="block text-gray-300 text-sm font-bold mb-3 flex items-center space-x-2">
                                <i className="fas fa-heading text-blue-400"></i>
                                <span>Order Title *</span>
                            </label>
                            <input
                                type="text"
                                id="orderTitle"
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/10 hover:border-white/20"
                                placeholder="e.g., Custom Police MDT Script with Real-time Data"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                            />
                        </div>
                        
                        <div className="relative">
                            <label htmlFor="orderDescription" className="block text-gray-300 text-sm font-bold mb-3 flex items-center space-x-2">
                                <i className="fas fa-align-left text-purple-400"></i>
                                <span>Detailed Description *</span>
                            </label>
                            <textarea
                                id="orderDescription"
                                rows="6"
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/10 hover:border-white/20 resize-none"
                                placeholder="Describe your vision in detail:
• What should the script do?
• What features are essential?
• Any specific design requirements?
• Reference materials or inspiration?
• Technical specifications?

The more details you provide, the better we can bring your idea to life!"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                required
                            ></textarea>
                            <div className="mt-2 text-right">
                                <span className={`text-xs font-medium ${
                                    description.length > 2000 ? 'text-red-400' :
                                    description.length > 1500 ? 'text-yellow-400' :
                                    'text-gray-500'
                                }`}>
                                    {description.length}/2000 characters
                                </span>
                            </div>
                        </div>
                        
                        <div className="relative">
                            <label htmlFor="orderBudget" className="block text-gray-300 text-sm font-bold mb-3 flex items-center space-x-2">
                                <i className="fas fa-dollar-sign text-green-400"></i>
                                <span>Budget / Price Expectation *</span>
                            </label>
                            <input
                                type="text"
                                id="orderBudget"
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/10 hover:border-white/20"
                                placeholder="e.g., $150-$300, Negotiable, Contact for Quote"
                                value={budget}
                                onChange={(e) => setBudget(e.target.value)}
                                required
                            />
                            <div className="mt-2 text-xs text-gray-400">
                                💡 Tip: Providing a realistic budget range helps us match you with the right developer
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex space-x-4 mt-10 animate-slideInLeft" style={{ animationDelay: '0.5s' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 group relative overflow-hidden bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-bold py-4 px-6 rounded-xl transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-gray-500/25 hover:-translate-y-1"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-gray-400/20 to-gray-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative flex items-center justify-center space-x-2">
                                <i className="fas fa-times group-hover:rotate-90 transition-transform duration-300"></i>
                                <span>Cancel</span>
                            </div>
                        </button>
                        
                        <button
                            type="button"
                            onClick={handleSave}
                            className="flex-1 group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold py-4 px-6 rounded-xl transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-blue-500/30 hover:-translate-y-1 hover:scale-105"
                        >
                            {/* Shimmer overlay */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            
                            <div className="relative flex items-center justify-center space-x-2">
                                <i className={`fas ${orderToEdit ? 'fa-save' : 'fa-paper-plane'} group-hover:scale-110 transition-transform duration-300`}></i>
                                <span>{orderToEdit ? 'Save Changes' : 'Submit Order'}</span>
                                <div className="w-2 h-2 rounded-full bg-white/60 opacity-0 group-hover:opacity-100 animate-pulse transition-opacity duration-300"></div>
                            </div>
                            
                            {/* Glow effect */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main CustomOrders Component ---
export function CustomOrders({ user, db, setModalMessage, onShowCustomOrderModal, onShowCustomOrderChatModal }) {
    const [customOrders, setCustomOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [errorOrders, setErrorOrders] = useState(null);
    // Removed chat modal state - handled in App.js

    // Fetch custom orders for the current user
    useEffect(() => {
        if (!db || !user?.uid) {
            setErrorOrders("Firestore database or user ID not available.");
            setLoadingOrders(false);
            return;
        }

        const ordersCollectionRef = collection(db, `users/${user.uid}/customOrders`);
        const q = query(ordersCollectionRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedOrders = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setCustomOrders(fetchedOrders);
            setLoadingOrders(false);
        }, (err) => {
            console.error("Error fetching custom orders:", err);
            setErrorOrders(`Failed to load custom orders: ${err.message}`);
            setModalMessage(`Failed to load custom orders: ${err.message}`);
            setLoadingOrders(false);
        });

        return () => unsubscribe();
    }, [db, user?.uid, setModalMessage]);

    // Handle deleting a custom order
    const handleDeleteOrder = useCallback(async (orderId, orderTitle) => {
        if (!db || !user?.uid) {
            setModalMessage("Error: Database or user ID not available.");
            return;
        }
        if (!window.confirm(`Are you sure you want to delete the custom order "${orderTitle}"? This cannot be undone.`)) {
            return;
        }
        try {
            const orderDocRef = doc(db, `users/${user.uid}/customOrders`, orderId);
            await deleteDoc(orderDocRef);
            setModalMessage(`Custom order "${orderTitle}" deleted successfully!`);
        } catch (error) {
            console.error("Error deleting custom order:", error);
            setModalMessage(`Failed to delete order: ${error.message}`);
        }
    }, [db, user?.uid, setModalMessage]);

    // Function to get status styling
    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-600';
            case 'in progress': return 'bg-blue-600';
            case 'completed': return 'bg-green-600';
            case 'cancelled': return 'bg-red-600';
            default: return 'bg-gray-600';
        }
    };

    if (loadingOrders) {
        return (
            <div className="glass-card p-8 rounded-2xl h-full flex items-center justify-center animate-fadeIn">
                <div className="flex flex-col items-center">
                    <div className="relative mb-6">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500/20 border-t-blue-500 animate-glow"></div>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 animate-pulse"></div>
                    </div>
                    <div className="text-center">
                        <p className="text-xl font-semibold gradient-text mb-2 animate-shimmer">Loading Custom Orders</p>
                        <p className="text-gray-400 animate-fadeIn" style={{ animationDelay: '0.3s' }}>Fetching your development requests...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (errorOrders) {
        return (
            <div className="glass-card p-8 rounded-2xl h-full flex items-center justify-center animate-fadeIn">
                <div className="text-center">
                    <div className="relative mb-6">
                        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-red-500/20 to-pink-600/20 flex items-center justify-center animate-bounceIn">
                            <i className="fas fa-exclamation-triangle text-red-400 text-3xl animate-pulse"></i>
                        </div>
                    </div>
                    <h3 className="text-2xl font-bold text-red-400 mb-4 animate-slideInLeft">Connection Error</h3>
                    <p className="text-lg text-gray-300 mb-2 animate-slideInLeft" style={{ animationDelay: '0.1s' }}>{errorOrders}</p>
                    <p className="text-sm text-gray-400 max-w-md mx-auto leading-relaxed animate-slideInLeft" style={{ animationDelay: '0.2s' }}>
                        Please check your Firestore connection and security rules for custom orders.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="glass-card p-8 rounded-2xl h-full flex flex-col min-h-[600px] animate-fadeIn">
            {/* Header Section */}
            <div className="flex items-center justify-between mb-8 animate-slideInLeft">
                <div className="relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl animate-pulse"></div>
                    <div className="relative">
                        <h3 className="text-4xl font-bold gradient-text mb-3 animate-slideInLeft">Custom Orders</h3>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2 text-gray-400 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                                <span className="text-sm font-medium">
                                    {customOrders.length > 0 ? `${customOrders.length} active order${customOrders.length > 1 ? 's' : ''}` : 'Ready for requests'}
                                </span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-500 text-xs animate-fadeIn" style={{ animationDelay: '0.3s' }}>
                                <i className="fas fa-clock"></i>
                                <span>Last updated: {new Date().toLocaleDateString()}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => onShowCustomOrderModal()}
                    className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-blue-500/30 hover:-translate-y-2 hover:scale-105 animate-bounceIn"
                    style={{ animationDelay: '0.4s' }}
                >
                    {/* Shimmer overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 animate-shimmer"></div>
                    
                    {/* Background pulse */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-heartbeat"></div>
                    
                    <div className="relative flex items-center space-x-3">
                        <div className="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center group-hover:rotate-180 transition-transform duration-500">
                            <i className="fas fa-plus text-sm"></i>
                        </div>
                        <span className="font-semibold">Create Order</span>
                        <div className="w-2 h-2 rounded-full bg-white/60 opacity-0 group-hover:opacity-100 animate-pulse transition-opacity duration-300"></div>
                    </div>
                    
                    {/* Glow effect */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
                </button>
            </div>

            {customOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center flex-grow text-center py-20 animate-fadeIn">
                    <div className="relative mb-8">
                        {/* Animated background rings */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 animate-ping" style={{ animationDuration: '3s' }}></div>
                        <div className="absolute inset-4 rounded-full bg-gradient-to-r from-blue-500/5 to-purple-500/5 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                        
                        {/* Main icon */}
                        <div className="relative w-32 h-32 mx-auto rounded-3xl bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center animate-float">
                            <i className="fas fa-shopping-basket text-6xl text-blue-400 animate-pulse"></i>
                        </div>
                        
                        {/* Floating particles */}
                        <div className="absolute top-4 left-4 w-2 h-2 rounded-full bg-blue-400/60 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="absolute top-8 right-6 w-1.5 h-1.5 rounded-full bg-purple-400/60 animate-bounce" style={{ animationDelay: '0.8s' }}></div>
                        <div className="absolute bottom-6 left-8 w-1 h-1 rounded-full bg-blue-300/60 animate-bounce" style={{ animationDelay: '1.2s' }}></div>
                    </div>
                    
                    <div className="space-y-4 max-w-md">
                        <h3 className="text-3xl font-bold gradient-text animate-slideInLeft" style={{ animationDelay: '0.3s' }}>
                            No Orders Yet
                        </h3>
                        <p className="text-gray-300 text-lg leading-relaxed animate-slideInLeft" style={{ animationDelay: '0.5s' }}>
                            Ready to bring your custom script ideas to life?
                        </p>
                        <p className="text-gray-400 text-sm leading-relaxed animate-slideInLeft" style={{ animationDelay: '0.7s' }}>
                            Submit your first custom development request and work directly with our developers through our integrated chat system.
                        </p>
                        
                        {/* Feature highlights */}
                        <div className="grid grid-cols-2 gap-4 mt-8 animate-slideInLeft" style={{ animationDelay: '0.9s' }}>
                            <div className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1">
                                <i className="fas fa-comments text-blue-400 text-xl mb-2"></i>
                                <p className="text-xs text-gray-300 font-medium">Real-time Chat</p>
                            </div>
                            <div className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1">
                                <i className="fas fa-code text-purple-400 text-xl mb-2"></i>
                                <p className="text-xs text-gray-300 font-medium">Custom Scripts</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 flex-grow">
                    {customOrders.map((order, index) => (
                        <div 
                            key={order.id} 
                            className="group glass-card border border-white/10 hover:border-blue-400/50 rounded-2xl p-8 transition-all duration-700 hover:-translate-y-3 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25 animate-fadeIn hover-lift relative overflow-hidden"
                            style={{ animationDelay: `${index * 0.1}s` }}
                        >
                            {/* Shimmer overlay */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            
                            {/* Status Badge with enhanced styling */}
                            <div className="flex items-center justify-between mb-6">
                                <div className="relative">
                                    <span className={`px-4 py-2 rounded-xl text-sm font-bold ${getStatusStyle(order.status)} text-white flex items-center space-x-2 shadow-lg group-hover:shadow-xl transition-all duration-300 animate-pulse`}>
                                        <div className="relative">
                                            <i className={`fas ${
                                                order.status === 'pending' ? 'fa-clock' :
                                                order.status === 'in progress' ? 'fa-spinner fa-spin' :
                                                order.status === 'completed' ? 'fa-check' :
                                                order.status === 'cancelled' ? 'fa-times' : 'fa-question'
                                            } text-sm`}></i>
                                        </div>
                                        <span>{order.status.toUpperCase()}</span>
                                    </span>
                                    {order.status === 'in progress' && (
                                        <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl opacity-30 animate-ping"></div>
                                    )}
                                </div>
                                <div className="text-right">
                                    <span className="text-gray-400 text-xs font-medium">Created</span>
                                    <p className="text-gray-300 text-sm font-semibold">
                                        {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                            </div>

                            {/* Order Title with enhanced hover effect */}
                            <div className="mb-6">
                                <h4 className="text-2xl font-bold text-white mb-3 leading-tight line-clamp-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 group-hover:bg-clip-text transition-all duration-500">
                                    {order.title}
                                </h4>
                                <div className="h-1 w-0 bg-gradient-to-r from-blue-400 to-purple-400 group-hover:w-full transition-all duration-500 rounded-full"></div>
                            </div>

                            {/* Order Description with fade effect */}
                            <div className="mb-8">
                                <p className="text-gray-300 text-sm leading-relaxed line-clamp-3 group-hover:text-gray-200 transition-colors duration-300">
                                    {order.description}
                                </p>
                            </div>

                            {/* Budget Display with enhanced styling */}
                            <div className="mb-8">
                                <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/20 group-hover:border-green-400/50 transition-all duration-300">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                        <i className="fas fa-dollar-sign text-green-400 text-lg animate-pulse"></i>
                                    </div>
                                    <div className="flex-grow">
                                        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Budget Range</p>
                                        <p className="text-green-400 font-bold text-lg">{order.budget}</p>
                                    </div>
                                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                                </div>
                            </div>

                            {/* Action Buttons with staggered animations */}
                            <div className="flex space-x-3">
                                <button
                                    onClick={() => onShowCustomOrderChatModal(order)}
                                    className="flex-1 group/btn relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold py-4 px-6 rounded-xl transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-blue-500/30 hover:-translate-y-1 hover:scale-105"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                    <div className="relative flex items-center justify-center space-x-2">
                                        <i className="fas fa-comments text-lg group-hover/btn:scale-110 transition-transform duration-300"></i>
                                        <span>Chat</span>
                                    </div>
                                </button>
                                
                                <button
                                    onClick={() => onShowCustomOrderModal(order)}
                                    className="group/btn relative overflow-hidden bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white font-bold py-4 px-4 rounded-xl transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-yellow-500/30 hover:-translate-y-1 hover:scale-110"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                    <div className="relative">
                                        <i className="fas fa-edit text-lg group-hover/btn:rotate-12 transition-transform duration-300"></i>
                                    </div>
                                </button>
                                
                                <button
                                    onClick={() => handleDeleteOrder(order.id, order.title)}
                                    className="group/btn relative overflow-hidden bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white font-bold py-4 px-4 rounded-xl transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-red-500/30 hover:-translate-y-1 hover:scale-110"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-red-400/20 to-pink-400/20 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                    <div className="relative">
                                        <i className="fas fa-trash text-lg group-hover/btn:scale-125 group-hover/btn:rotate-12 transition-transform duration-300"></i>
                                    </div>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal rendering removed - handled in App.js */}
        </div>
    );
}

export default CustomOrders;
